import { SessionExpiredError } from "../../types";
import { PermissionError } from "../errors";
import type { IdentityApi } from "../types";
import type { LiveCtx } from "./auth";
import { ROUTES } from "./routes";
import {
  parseAccessRequest,
  parseInvitation,
  parseInvitations,
  parseMembers,
  parseOrganisation,
  parseSession,
} from "./schemas";

type OrganisationApi = Pick<
  IdentityApi,
  | "getOrganisation"
  | "updateOrganisation"
  | "completeOrganisationSetup"
  | "listMembers"
  | "listInvitations"
  | "invite"
  | "resendInvitation"
  | "revokeInvitation"
  | "requestOrganisationAccess"
>;

/**
 * The current organisation (27 §8 `/orgs/:id/...`; FR-ADMIN-009, 026, 027) and the public Request access form
 * (FR-ADMIN-052). Calls name the organisation the session is in; the service refuses any other.
 */
/** The organisation the session is in; the service refuses calls about any other. */
export async function currentOrganisationId(ctx: LiveCtx): Promise<string> {
  const known = ctx.latest();
  const session =
    known === undefined ? ctx.remember(await ctx.http.get(ROUTES.me, parseSession)) : known;
  if (session === null) throw new SessionExpiredError();
  if (session.organisation === undefined)
    throw new PermissionError("You aren't a member of an organisation on Orthos.");
  return session.organisation.id;
}

export function createOrganisationApi(ctx: LiveCtx): OrganisationApi {
  const { http } = ctx;
  const organisationId = () => currentOrganisationId(ctx);

  return {
    async getOrganisation() {
      return http.get(ROUTES.organisation(await organisationId()), parseOrganisation);
    },

    async updateOrganisation(patch) {
      const id = await organisationId();
      return http.send("PATCH", ROUTES.organisation(id), patch, parseOrganisation);
    },

    async completeOrganisationSetup(patch) {
      const id = await organisationId();
      const session = await http.send("POST", ROUTES.setup(id), patch, parseSession);
      ctx.remember(session);
      ctx.signals.announce();
      return session;
    },

    async listMembers() {
      return http.get(ROUTES.members(await organisationId()), parseMembers);
    },

    async listInvitations() {
      return http.get(ROUTES.invitations(await organisationId()), parseInvitations);
    },

    async invite(emails, role) {
      const id = await organisationId();
      return http.send("POST", ROUTES.invitations(id), { emails, role }, parseInvitations);
    },

    async resendInvitation(invitationId) {
      const id = await organisationId();
      return http.send("POST", ROUTES.invitation(id, invitationId, "resend"), {}, parseInvitation);
    },

    async revokeInvitation(invitationId) {
      const id = await organisationId();
      await http.sendOnly("POST", ROUTES.invitation(id, invitationId, "revoke"), {});
    },

    async requestOrganisationAccess(input) {
      return http.send("POST", ROUTES.accessRequests, input, parseAccessRequest);
    },
  };
}
